import React, { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  GetProcurementProcessesApiArg,
  NewProcurementProcess,
  PatchProcurementProcessesByIdApiArg,
  PostCreateProcurementProcessApiArg,
  ProcurementProcessWithRelations,
  useGetProcurementProcessesQuery,
  useLazyGetProcurementProcessesQuery,
  usePatchProcurementProcessesByIdMutation,
  useGetUsersMeQuery,
  usePostCreateProcurementProcessMutation,
} from '../../../redux/vmsApi';
import * as XLSX from 'xlsx';
import RequisitionsFilter from '../common/RequisitionsFilter';
import { getCurrentDate } from "../../../utils/dateUtil";
import { camelCaseToSpaced, formatJobRole } from '../../../utils/formattingUtil';
import { useRouter } from "../../../hooks/useRouter";

const ProcurementProcessList: React.FC = () => {
  const router = useRouter();
  const theme = useTheme();
  const isTabletOrSmaller = useMediaQuery(theme.breakpoints.down('md'));

  const [currentOpenRow, setCurrentOpenRow] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openedInNewTabRows, setOpenedInNewTabRows] = useState<Record<string, boolean>>({});

  const [postProcurementProcess] = usePostCreateProcurementProcessMutation();
  const [patchProcurementProcessById] = usePatchProcurementProcessesByIdMutation();
  const [triggerProcurementProcessesExport] = useLazyGetProcurementProcessesQuery();
  const [isExporting, setIsExporting] = useState(false);

  const [filters, setFilters] = useState({
    title: '',
    requestType: 'all',
    approvedBy: '',
    development: '',
    reqNumber: '',
    fromDate: '2025-01-06',
    toDate: getCurrentDate(),
    currentTask: 'all',
    rejectedBy: '',
    procurementOfficer: '',
    reportByEmail: false,
    reportName: '',
  });

  const handleToggleDropdown = (rowId: string) => {
    setCurrentOpenRow((prev) => (prev === rowId ? null : rowId));
  };

  const handleSplitClick = async (procurementProcess: any) => {
    setIsSubmitting(true);
    const newProcurementProcess: NewProcurementProcess = {
      currentTask: "PCToUploadQuotations",
      previousTask: "ProjectManagerReviewPurchaseRequisition",
      currentTaskOwner: "procurement-clerk",
      priority: procurementProcess.priority || 'Standard',
      initiatedDate: procurementProcess.initiatedDate,
      lastActionedDate: procurementProcess.lastActionedDate,
      initiatedByUser: procurementProcess.initiatedByUser,
      requisition: {
        id: procurementProcess.requisition.id,
        title: procurementProcess.requisition.title,
        development: {
          id: procurementProcess.requisition.development.id,
          name: procurementProcess.requisition.development.name,
        },
        project: {
          id: procurementProcess.requisition.project.id,
          name: procurementProcess.requisition.project.name,
        },
        totalCost: procurementProcess.requisition.totalCost,
        reqNumber: procurementProcess.requisition.reqNumber,
        approved: procurementProcess.requisition.approved,
        items: procurementProcess.requisition.items.map((item: any) => ({
          itemCode:item.itemCode,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          procured: item.procured,
          rate: item.rate,
          totalCostPerItem: item.totalCostPerItem,
          discountPerItem: item.discountPerItem,
          VAT: item.VAT,
        })),
        documents: procurementProcess.requisition.documents.map((doc: any) => ({
          name: doc.name,
          url: doc.url,
        })),
      },
      siteManagerReview: {
        user: procurementProcess.siteManagerReview.user,
        comment: procurementProcess.siteManagerReview.comment,
        approved: procurementProcess.siteManagerReview.approved,
        dateReviewed: procurementProcess.siteManagerReview.dateReviewed,
        quotations: Array.isArray(procurementProcess.siteManagerReview.quotations)
          ? procurementProcess.siteManagerReview.quotations
          : [],
      },
      projectManagerReview: {
        user: {
          ...procurementProcess.projectManagerReview.user,
          previousToken:
            procurementProcess.projectManagerReview.user.previousToken || "",
        },
        comment: procurementProcess.projectManagerReview.comment,
        approved: procurementProcess.projectManagerReview.approved,
        dateReviewed: procurementProcess.projectManagerReview.dateReviewed,
        quotations: Array.isArray(procurementProcess.projectManagerReview.quotations)
          ? procurementProcess.projectManagerReview.quotations
          : [],
      },
      status: "New Requisition",
      requisitionDocument: { name: procurementProcess.requisitionDocument?.name, url: procurementProcess.requisitionDocument.url },
      purchaseOrderDocument: { name: "", url: "" },
      splitPurchaseOrder: true
    };

    const apiArg: PostCreateProcurementProcessApiArg = {
      newProcurementProcess,
    };

    try {
      await postProcurementProcess(apiArg).unwrap();
      alert(" procurement process split successfully!");

    } catch (error) {
      console.error("Error creating procurement process:", error);
      alert("Failed to split procurement process. Please try again.");
    } finally {
      setCurrentOpenRow(null);
      setIsSubmitting(false);
    }
  };

  const handleSetPriority = async (
    procurementProcess: ProcurementProcessWithRelations,
    nextPriority: 'Standard' | 'Urgent' | 'Critical',
  ) => {
    if (!procurementProcess.id) {
      return;
    }

    const apiArg: PatchProcurementProcessesByIdApiArg = {
      id: procurementProcess.id,
      procurementProcessPartial: {
        priority: nextPriority,
      },
    };

    try {
      await patchProcurementProcessById(apiArg).unwrap();
    } catch (error) {
      console.error('Failed to update procurement process priority', error);
      alert('Failed to update priority. Please try again.');
    } finally {
      setCurrentOpenRow(null);
    }
  };

  const handleViewProcessDetails = (procurementProcessId: string | undefined) => {
    router.push(`/procurement/procurementProcess/${procurementProcessId}`)
    setCurrentOpenRow(null);
  };

  const [apiParams, setApiParams] = useState<GetProcurementProcessesApiArg>({ ...filters, skip: 0, limit: 10 });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data: user } = useGetUsersMeQuery();
  const { data: procurementProcesses, isLoading, isError } = useGetProcurementProcessesQuery(apiParams);
  const isTaskForceUser = user?.roles?.includes('task-force') ?? false;

  const handleFilterChange = (field: string, value: any) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setApiParams({
      ...filters,
      skip: 0,
      limit: pageSize,
    });
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setApiParams((prevParams) => ({
      ...prevParams,
      skip: newPage * pageSize,
    }));
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
    setApiParams((prevParams) => ({
      ...prevParams,
      limit: newSize,
      skip: 0,
    }));
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      const exportData = await triggerProcurementProcessesExport({
        ...apiParams,
        reportByEmail: false,
        reportName: '',
        skip: 0,
        limit: 200000,
      }).unwrap();

      const rows = (exportData ?? []).map((procurementProcess: ProcurementProcessWithRelations, index: number) => ({
        number: index + 1,
        reqNumber: procurementProcess.requisition?.reqNumber ?? '',
        title: procurementProcess.requisition?.title ?? '',
        requisitionRaisedBy: procurementProcess.initiatedByUser?.name ?? '',
        dateRaised: procurementProcess.initiatedDate
          ? new Date(procurementProcess.initiatedDate).toISOString()
          : '',
        currentTask: procurementProcess.currentTask ?? '',
        currentTaskOwner: procurementProcess.currentTaskOwner ?? '',
        previousTask: procurementProcess.previousTask ?? '',
        development: procurementProcess.requisition?.development?.name ?? '',
        project: procurementProcess.requisition?.project?.name ?? '',
        status: procurementProcess.status ?? '',
        requestType: procurementProcess.requestType ?? '',
        priority: procurementProcess.priority ?? 'Standard',
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ProcurementProcesses');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      XLSX.writeFile(workbook, `procurement-processes-${timestamp}.xlsx`);
    } catch (error) {
      console.error('Failed to export procurement processes Excel', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleNavigate = (task: string, id: string) => {
    window.location.href = `/procurement/${task}/${id}`;
  };

  const handleTaskOpenInNewTab = (procurementProcessId?: string) => {
    if (!procurementProcessId) {
      return;
    }

    setOpenedInNewTabRows((prev) => ({
      ...prev,
      [procurementProcessId]: true,
    }));
  };

  const isButtonDisabled = (currentTaskOwner: string | undefined, currentTask?: string) => {
    if (user?.roles?.includes('admin')) {
      return false;
    }
    if (currentTask === 'ProjectManagerReviewPurchaseRequisition') {
      return !user?.roles?.includes('site-manager');
    }
    return !user?.roles?.includes(currentTaskOwner || '');
  };

  const getStatusLabel = (status?: string) => {
    if (status === "New Requisition") return "Status: Requisition Awaiting approval";
    if (status === "New Purchase Order") return "Status: Procurement Application Awaiting approval";
    if (status === "Payment Requisition") return "Waiting for financial clearance";
    if (status === "COO To Approve Payment Requisitions") return "Waiting for financial clearance";
    if (status === "CEO To Approve Payment Requisitions") return "Waiting for financial clearance";
    if (status === "CPO To Prioritize Payment Requisitions") return "Waiting for financial clearance";
    if (status === "CFO To Schedule Payment Requisitions") return "Waiting for financial clearance";
    if (status === "Payment Requisitions Rejected") return "Payment Requisitions Rejected";
    if (status === "Cancelled") return "Status: Process Cancelled";
    if (status === "Closed") return "Status: Process Closed";
    if (status === "Pocess Complete") return "Status: Pocess Complete";
    return "";
  };

  const getCurrentTaskLabel = (task?: string) => {
    if (task === 'SiteManagerReviewPurchaseRequisition') return 'FIRST LEVEL APPROVAL';
    if (task === 'ProjectManagerReviewPurchaseRequisition') return 'FIRST LEVEL APPROVAL';
    if (task === 'CPOPicksSupplier') return 'HOD TO PICK SUPPLIER';
    if (task === 'CFOAllocateFunds') return 'FM To Review Compliance';
    if (task === 'PCToUploadQuotations') return 'PO To Upload Quotations';
    return camelCaseToSpaced(task || 'N/A', true);
  };

  const getCurrentTaskOwnerLabel = (currentTaskOwner?: string, currentTask?: string) => {
    if (currentTask === 'ProjectManagerReviewPurchaseRequisition') return 'Site Manager';
    if (currentTaskOwner === 'cfo') return 'Procurement Admin & Finance Manager';
    if (currentTaskOwner === 'CPO' || currentTaskOwner === 'cpo') return 'HOD';
    if (currentTaskOwner === 'procurement-clerk') return 'Procurement Officer';
    if (currentTaskOwner) return formatJobRole(currentTaskOwner);
    return 'N/A';
  };



  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error fetching Procurement Processes.</div>;
  }

  return (
    <React.Fragment>
      <RequisitionsFilter
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={handleApplyFilters}
        actionButton={(
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={isExporting}
            onClick={handleExportExcel}
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          >
            {isExporting ? 'Exporting...' : 'Export Excel (All Filtered)'}
          </Button>
        )}
      />

      {isTabletOrSmaller ? (
        <Box display="flex" flexDirection="column" gap={2}>
          {procurementProcesses && procurementProcesses.length > 0 ? (
            procurementProcesses.map((procurementProcess: ProcurementProcessWithRelations, index: number) => (
              <Card key={procurementProcess.id} sx={{ boxShadow: 3, borderRadius: 2, backgroundColor: index % 2 === 0 ? '#f0f4f7' : 'white' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {procurementProcess.requisition?.reqNumber || 'N/A'}
                    </Typography>
                    {procurementProcess.splitPurchaseOrder && (
                      <Box
                        sx={{
                          backgroundColor: 'blue',
                          color: 'white',
                          px: 1,
                          py: 0.25,
                          borderRadius: '8px',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                        }}
                      >
                        SPLIT
                      </Box>
                    )}
                  </Box>

                  <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                    {procurementProcess.priority === 'Urgent' && (
                      <Chip
                        icon={<PriorityHighIcon sx={{ fontSize: '0.8rem !important' }} />}
                        label="URGENT"
                        size="small"
                        sx={{
                          backgroundColor: '#FF8C00',
                          color: '#fff',
                          fontWeight: 'bold',
                          '& .MuiChip-icon': { color: '#fff' },
                        }}
                      />
                    )}
                    {procurementProcess.priority === 'Critical' && (
                      <Chip
                        icon={<ErrorOutlineIcon sx={{ fontSize: '0.8rem !important' }} />}
                        label="CRITICAL"
                        size="small"
                        sx={{
                          backgroundColor: '#D32F2F',
                          color: '#fff',
                          fontWeight: 'bold',
                          '& .MuiChip-icon': { color: '#fff' },
                        }}
                      />
                    )}
                    {index < 3 && (
                      <Chip
                        label="NEW"
                        size="small"
                        sx={{ backgroundColor: 'red', color: 'white', fontWeight: 'bold' }}
                      />
                    )}
                  </Box>

                  <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Title:</strong> {procurementProcess.requisition?.title || 'N/A'}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Raised By:</strong> {procurementProcess.initiatedByUser?.name || 'N/A'}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.75 }}>
                    <strong>Date Raised:</strong> {procurementProcess.initiatedDate
                      ? new Date(procurementProcess.initiatedDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: '2-digit',
                      })
                      : 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Current Task Owner:</strong> {getCurrentTaskOwnerLabel(procurementProcess.currentTaskOwner, procurementProcess.currentTask)}</Typography>
                  <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Previous Task:</strong> {camelCaseToSpaced(procurementProcess.previousTask || 'N/A', true)}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}><strong>Development:</strong> {procurementProcess.requisition?.development?.name || 'N/A'}, {procurementProcess.requisition?.project?.name || 'N/A'}</Typography>

                  {procurementProcess.status && (
                    <Box
                      sx={{
                        backgroundColor: procurementProcess.status === "Cancelled" ? "gold" : "green",
                        color: "white",
                        px: 1,
                        py: 0.5,
                        borderRadius: "8px",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        fontStyle: 'italic',
                        mb: 1,
                        display: "inline-flex",
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <WatchLaterIcon sx={{ fontSize: '0.9rem' }} />
                      {getStatusLabel(procurementProcess.status)}
                    </Box>
                  )}

                  <Box display="flex" flexDirection="column" gap={1} mt={1}>
                    <Button
                      component="a"
                      href={`/procurement/${procurementProcess.currentTask}/${procurementProcess.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleTaskOpenInNewTab(procurementProcess.id)}
                      disabled={isButtonDisabled(procurementProcess.currentTaskOwner, procurementProcess.currentTask)}
                      sx={{
                        backgroundColor: '#F7C914',
                        color: 'white',
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: '#1976D2' },
                      }}
                      startIcon={isButtonDisabled(procurementProcess.currentTaskOwner, procurementProcess.currentTask) ? null : <ArrowForwardIcon />}
                    >
                      {getCurrentTaskLabel(procurementProcess.currentTask || undefined)}
                    </Button>

                    {openedInNewTabRows[procurementProcess.id || ''] && (
                      <Chip
                        icon={<CheckCircleOutlineIcon sx={{ fontSize: '0.85rem !important' }} />}
                        label="Opened In New Tab"
                        size="small"
                        sx={{
                          alignSelf: 'flex-start',
                          backgroundColor: '#2E7D32',
                          color: '#fff',
                          fontWeight: 'bold',
                          '& .MuiChip-icon': { color: '#fff' },
                        }}
                      />
                    )}

                    <Button
                      component="a"
                      href={`/procurement/procurementProcess/${procurementProcess.id}`}
                      variant="outlined"
                      onClick={() => handleViewProcessDetails(procurementProcess.id)}
                    >
                      View
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => handleSplitClick(procurementProcess)}
                      disabled={isSubmitting}
                    >
                      Split
                    </Button>

                    {isTaskForceUser && (
                      <Button
                        startIcon={<PriorityHighIcon />}
                        sx={{
                          color: procurementProcess.priority === 'Urgent' ? '#fff' : '#FF8C00',
                          backgroundColor: procurementProcess.priority === 'Urgent' ? '#FF8C00' : 'transparent',
                          fontWeight: 'bold',
                          border: '1px solid #FF8C00',
                          "&:hover": { backgroundColor: '#FF8C00', color: '#fff' },
                        }}
                        onClick={() => {
                          const nextPriority = procurementProcess.priority === 'Urgent' ? 'Standard' : 'Urgent';
                          void handleSetPriority(procurementProcess, nextPriority);
                        }}
                      >
                        {procurementProcess.priority === 'Urgent' ? 'Remove Urgent' : 'Mark as Urgent'}
                      </Button>
                    )}

                    {isTaskForceUser && (
                      <Button
                        startIcon={<ErrorOutlineIcon />}
                        sx={{
                          color: procurementProcess.priority === 'Critical' ? '#fff' : '#D32F2F',
                          backgroundColor: procurementProcess.priority === 'Critical' ? '#D32F2F' : 'transparent',
                          fontWeight: 'bold',
                          border: '1px solid #D32F2F',
                          "&:hover": { backgroundColor: '#D32F2F', color: '#fff' },
                        }}
                        onClick={() => {
                          const nextPriority = procurementProcess.priority === 'Critical' ? 'Standard' : 'Critical';
                          void handleSetPriority(procurementProcess, nextPriority);
                        }}
                      >
                        {procurementProcess.priority === 'Critical' ? 'Remove Critical' : 'Mark as Critical'}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))
          ) : (
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography>No matching Procurement Processes found.</Typography>
            </Paper>
          )}
        </Box>
      ) : (
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Title</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Requisition Raised By</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Date Raised</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Current Task</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Current Task Owner</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Previous Task</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Development</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {procurementProcesses && procurementProcesses.length > 0 ? (
              procurementProcesses.map((procurementProcess: ProcurementProcessWithRelations, index: number) => (
                <TableRow key={procurementProcess.id} sx={{ backgroundColor: index % 2 === 0 ? '#f0f4f7' : 'white' }}>
                  <TableCell component="th" scope="row">
                    {procurementProcess.requisition?.reqNumber || 'N/A'}
                    {procurementProcess.splitPurchaseOrder && (
                      <Box
                        sx={{
                          backgroundColor: 'blue',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '8px',
                          fontSize: '0.7rem',
                          fontWeight: 'bold',
                          marginTop: '4px',
                          display: 'inline-block',
                        }}
                      >
                        SPLIT
                      </Box>
                    )}
                    {procurementProcess.priority === 'Urgent' && (
                      <Chip
                        icon={<PriorityHighIcon sx={{ fontSize: '0.8rem !important' }} />}
                        label="URGENT"
                        size="small"
                        sx={{
                          ml: 0.5,
                          backgroundColor: '#FF8C00',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                          height: '20px',
                          '& .MuiChip-icon': { color: '#fff' },
                        }}
                      />
                    )}
                    {procurementProcess.priority === 'Critical' && (
                      <Chip
                        icon={<ErrorOutlineIcon sx={{ fontSize: '0.8rem !important' }} />}
                        label="CRITICAL"
                        size="small"
                        sx={{
                          ml: 0.5,
                          backgroundColor: '#D32F2F',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                          height: '20px',
                          '& .MuiChip-icon': { color: '#fff' },
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {procurementProcess.requisition?.title || 'N/A'}
                  </TableCell>

                  <TableCell align="right">
                    {procurementProcess.initiatedByUser?.name || 'N/A'}
                    {procurementProcess.status && (
                      <Box
                        sx={{
                          backgroundColor:
                            procurementProcess.status === "Cancelled" ? "gold" : "green",
                          color: "white",
                          padding: "2px 6px",
                          borderRadius: "8px",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                          fontStyle: 'italic',
                          marginTop: "4px",
                          display: "inline-block",
                        }}
                      >
                        <WatchLaterIcon sx={{ fontSize: '0.9rem' }} />
                        {procurementProcess.status === "New Requisition"
                          ? "Status: Requisition Awaiting approval"
                          : procurementProcess.status === "New Purchase Order"
                            ? "Status: Procurement Application Awaiting approval"
                            : procurementProcess.status === "Payment Requisition"
                              ? "Waiting for financial clearance"
                              : procurementProcess.status === "COO To Approve Payment Requisitions"
                                ? "Waiting for financial clearance"
                                : procurementProcess.status === "CEO To Approve Payment Requisitions"
                                  ? "Waiting for financial clearance"
                                  : procurementProcess.status === "CPO To Prioritize Payment Requisitions"
                                    ? "Waiting for financial clearance"
                                    : procurementProcess.status === "CFO To Schedule Payment Requisitions"
                                      ? "Waiting for financial clearance"
                                      : procurementProcess.status === "Payment Requisitions Rejected"
                                        ? "Payment Requisitions Rejected"
                                        : procurementProcess.status === "Cancelled"
                                          ? "Status: Process Cancelled"
                                          : procurementProcess.status === "Closed"
                                            ? "Status: Process Closed"
                                          : procurementProcess.status === "Pocess Complete"
                                            ? "Status: Pocess Complete"
                                            : ""}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {procurementProcess.initiatedDate ?
                      new Date(procurementProcess.initiatedDate).toLocaleDateString(
                        'en-GB',
                        {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit',
                        }
                      ) : 'N/A'}

                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ position: 'relative', display: 'inline-block' }}>

                      {index < 3 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            backgroundColor: 'red',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            zIndex: 1,
                          }}
                        >
                          NEW
                        </Box>
                      )}


                      <Button
                        component="a"
                        href={`/procurement/${procurementProcess.currentTask}/${procurementProcess.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleTaskOpenInNewTab(procurementProcess.id)}
                        disabled={isButtonDisabled(procurementProcess.currentTaskOwner, procurementProcess.currentTask)}
                        sx={{
                          backgroundColor: '#F7C914',
                          color: 'white',
                          borderRadius: '8px',
                          position: 'relative',
                          '&:hover': {
                            backgroundColor: '#1976D2',
                          },
                        }}
                        startIcon={
                          isButtonDisabled(procurementProcess.currentTaskOwner, procurementProcess.currentTask) ? null : (
                            <ArrowForwardIcon />
                          )
                        }
                      >
                        {getCurrentTaskLabel(procurementProcess.currentTask || undefined)}
                      </Button>

                      {openedInNewTabRows[procurementProcess.id || ''] && (
                        <Chip
                          icon={<CheckCircleOutlineIcon sx={{ fontSize: '0.8rem !important' }} />}
                          label="Opened In New Tab"
                          size="small"
                          sx={{
                            mt: 1,
                            backgroundColor: '#2E7D32',
                            color: '#fff',
                            fontWeight: 'bold',
                            height: '22px',
                            '& .MuiChip-icon': { color: '#fff' },
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {getCurrentTaskOwnerLabel(procurementProcess.currentTaskOwner, procurementProcess.currentTask)}

                  </TableCell>

                  <TableCell align="right">
                    {camelCaseToSpaced(procurementProcess.previousTask || 'N/A', true)}
                  </TableCell>
                  <TableCell align="right">
                    {procurementProcess.requisition?.development?.name || 'N/A'}, {procurementProcess.requisition?.project?.name || 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      <Button
                        sx={{
                          backgroundColor: "primary.main",
                          color: "white",
                          "&:hover": { backgroundColor: "primary.dark" },
                        }}
                        onClick={() => handleToggleDropdown(procurementProcess.id || "")}
                      >
                        Actions
                      </Button>
                      {currentOpenRow === procurementProcess.id && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            zIndex: 1,
                            mt: 1,
                            minWidth: "150px",
                            backgroundColor: "white",
                            boxShadow: 3,
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >

                          <Button
                            component="a"
                            href={`/procurement/procurementProcess/${procurementProcess.id}`}
                            sx={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              px: 2,
                              py: 1,
                              "&:hover": { backgroundColor: "grey.100" },
                            }}
                            onClick={() => handleViewProcessDetails(procurementProcess.id)}
                          >
                            View
                          </Button>
                          <Button
                            sx={{
                              display: "block",
                              width: "100%",
                              textAlign: "left",
                              px: 2,
                              py: 1,
                              "&:hover": { backgroundColor: "grey.100" },
                            }}
                            onClick={() => handleSplitClick(procurementProcess)}
                            disabled={isSubmitting}
                          >
                            Split
                          </Button>

                          {/* Priority flags — UI only, logic to be implemented */}
                          {isTaskForceUser && (
                            <Button
                              startIcon={<PriorityHighIcon />}
                              sx={{
                                display: "block",
                                width: "100%",
                                textAlign: "left",
                                px: 2,
                                py: 1,
                                color: procurementProcess.priority === 'Urgent' ? '#fff' : '#FF8C00',
                                backgroundColor: procurementProcess.priority === 'Urgent' ? '#FF8C00' : 'transparent',
                                fontWeight: 'bold',
                                "&:hover": { backgroundColor: '#FF8C00', color: '#fff' },
                              }}
                              onClick={() => {
                                const nextPriority = procurementProcess.priority === 'Urgent' ? 'Standard' : 'Urgent';
                                void handleSetPriority(procurementProcess, nextPriority);
                              }}
                            >
                              {procurementProcess.priority === 'Urgent' ? 'Remove Urgent' : 'Mark as Urgent'}
                            </Button>
                          )}

                          {isTaskForceUser && (
                            <Button
                              startIcon={<ErrorOutlineIcon />}
                              sx={{
                                display: "block",
                                width: "100%",
                                textAlign: "left",
                                px: 2,
                                py: 1,
                                color: procurementProcess.priority === 'Critical' ? '#fff' : '#D32F2F',
                                backgroundColor: procurementProcess.priority === 'Critical' ? '#D32F2F' : 'transparent',
                                fontWeight: 'bold',
                                "&:hover": { backgroundColor: '#D32F2F', color: '#fff' },
                              }}
                              onClick={() => {
                                const nextPriority = procurementProcess.priority === 'Critical' ? 'Standard' : 'Critical';
                                void handleSetPriority(procurementProcess, nextPriority);
                              }}
                            >
                              {procurementProcess.priority === 'Critical' ? 'Remove Critical' : 'Mark as Critical'}
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">No matching Procurement Processes found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}


      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button
          onClick={() => handlePageChange(Math.max(page - 1, 0))}
          disabled={page === 0}
          variant="contained"
          sx={{ marginRight: 2 }}
        >
          Previous
        </Button>

        <Typography variant="body1">Page {page + 1}</Typography>

        <Button
          onClick={() => handlePageChange(page + 1)}
          disabled={procurementProcesses?.length === undefined || procurementProcesses.length < pageSize}
          variant="contained"
          sx={{ marginLeft: 2 }}
        >
          Next
        </Button>
      </Box>


      <Box mt={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body2" sx={{ marginRight: 2 }}>Rows per page:</Typography>
        <FormControl variant="outlined" size="small">
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            sx={{ backgroundColor: '#f0f4f7' }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </React.Fragment>
  );
};

export default ProcurementProcessList;
